import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import {
    Cloud,
    CloudRain,
    CloudLightning,
    Sun,
    Wind,
    Thermometer,
    Droplets,
    Eye,
    AlertOctagon,
    Moon,
    CloudFog,
    Snowflake
} from 'lucide-react';
import { weatherService, WeatherData } from '../../utils/services/weatherService';
import { Card } from '../ui/card';
import { Badge } from '../ui/badge';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../ui/tooltip';

interface WeatherOverlayProps {
    center: { lat: number; lng: number };
    visible: boolean;
}

export default function WeatherOverlay({ center, visible }: WeatherOverlayProps) {
    const [weather, setWeather] = useState<WeatherData | null>(null);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (!visible) return;

        let mounted = true;
        const fetchWeather = async () => {
            setLoading(true);
            try {
                const data = await weatherService.getCurrentWeather(center.lat, center.lng);
                if (mounted && data) {
                    setWeather(data);
                }
            } catch (error) {
                console.error('Failed to fetch weather', error);
            } finally {
                if (mounted) setLoading(false);
            }
        };

        fetchWeather();
        // Refresh every 10 minutes
        const interval = setInterval(fetchWeather, 600000);

        return () => {
            mounted = false;
            clearInterval(interval);
        };
    }, [center.lat, center.lng, visible]);

    if (!visible || !weather) return null;

    const getWeatherIcon = (icon: string) => {
        switch (icon) {
            case 'clear': return <Sun className="h-8 w-8 text-amber-400" />;
            case 'cloudy': return <Cloud className="h-8 w-8 text-slate-400" />;
            case 'rain': return <CloudRain className="h-8 w-8 text-blue-400" />;
            case 'storm': return <CloudLightning className="h-8 w-8 text-purple-400 animate-pulse" />;
            case 'fog': return <CloudFog className="h-8 w-8 text-slate-300" />;
            case 'snow': return <Snowflake className="h-8 w-8 text-cyan-200" />;
            default: return <Sun className="h-8 w-8 text-amber-400" />;
        }
    };

    const getSafetyStatus = () => {
        if (weather.isSevere) return { color: 'bg-red-500/20 text-red-500 border-red-500/50', text: 'SEVERE' };
        if (weather.isStormy) return { color: 'bg-amber-500/20 text-amber-500 border-amber-500/50', text: 'CAUTION' };
        return { color: 'bg-emerald-500/20 text-emerald-500 border-emerald-500/50', text: 'SAFE' };
    };

    const safety = getSafetyStatus();

    return (
        <AnimatePresence>
            <motion.div
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: 20 }}
                className="absolute top-20 right-4 z-40 pointer-events-auto"
            >
                <Card className="w-64 bg-slate-950/80 backdrop-blur-md border border-slate-700 shadow-xl overflow-hidden">
                    {/* Header with Safety Status */}
                    <div className="p-3 bg-slate-900/50 border-b border-slate-700 flex justify-between items-center">
                        <span className="text-[10px] uppercase font-bold text-slate-400 tracking-wider">Site Conditions</span>
                        <Badge variant="outline" className={`text-[10px] h-5 ${safety.color}`}>
                            {safety.text}
                        </Badge>
                    </div>

                    <div className="p-4">
                        <div className="flex items-center justify-between mb-4">
                            <div className="flex items-center gap-3">
                                {getWeatherIcon(weather.icon)}
                                <div>
                                    <div className="text-2xl font-bold text-white tracking-tight">{weather.temperature}°F</div>
                                    <div className="text-xs text-slate-400 capitalize">{weather.description}</div>
                                </div>
                            </div>
                        </div>

                        <div className="grid grid-cols-2 gap-2">
                            <TooltipProvider>
                                <Tooltip>
                                    <TooltipTrigger asChild>
                                        <div className="bg-slate-900/50 p-2 rounded border border-slate-800 flex items-center gap-2 group hover:border-slate-600 transition-colors cursor-help">
                                            <Wind className="h-4 w-4 text-blue-400" />
                                            <div>
                                                <div className="text-white text-sm font-bold">{weather.windSpeed} mph</div>
                                                <div className="text-[9px] text-slate-500">WIND {weather.windDirection}°</div>
                                            </div>
                                        </div>
                                    </TooltipTrigger>
                                    <TooltipContent side="left" className="bg-slate-900 border-slate-700 text-slate-300">
                                        <p>Wind Restrictions &gt; 35mph</p>
                                    </TooltipContent>
                                </Tooltip>
                            </TooltipProvider>

                            <div className="bg-slate-900/50 p-2 rounded border border-slate-800 flex items-center gap-2">
                                <Droplets className="h-4 w-4 text-cyan-400" />
                                <div>
                                    <div className="text-white text-sm font-bold">{weather.humidity}%</div>
                                    <div className="text-[9px] text-slate-500">HUMIDITY</div>
                                </div>
                            </div>

                            <div className="bg-slate-900/50 p-2 rounded border border-slate-800 flex items-center gap-2">
                                <Eye className="h-4 w-4 text-purple-400" />
                                <div>
                                    <div className="text-white text-sm font-bold">{weather.visibility} mi</div>
                                    <div className="text-[9px] text-slate-500">VISIBILITY</div>
                                </div>
                            </div>

                            {weather.precipitation > 0 && (
                                <div className="bg-slate-900/50 p-2 rounded border border-slate-800 flex items-center gap-2">
                                    <CloudRain className="h-4 w-4 text-blue-500" />
                                    <div>
                                        <div className="text-white text-sm font-bold">{weather.precipitation}"</div>
                                        <div className="text-[9px] text-slate-500">PRECIP</div>
                                    </div>
                                </div>
                            )}
                        </div>

                        <div className="mt-3 pt-2 border-t border-slate-700/50">
                            <p className="text-[9px] text-slate-500 text-right flex items-center justify-end gap-1">
                                Powered by Open-Meteo <Cloud className="h-3 w-3" />
                            </p>
                        </div>
                    </div>
                </Card>
            </motion.div>
        </AnimatePresence>
    );
}
